<template>
    <div class="link-interventions">
        <fe-dialog
            v-if="localValue && !showWarningState"
            title="Link an Intervention Plan to Decision Rationale"
            @dismiss="localValue=false; selectedIntervention = null"
            @accept="doLink"
            dismissButtonText="Cancel"
            :acceptButtonText="showErrorState ? 'Got It' : 'Link Intervention'"
            persistent
            width="800"
            height="600"
            disableAutoclose
        >
            <banner 
                v-if="showBanner"
                class="mt-2 mb-5"
                usage="warning" 
                text="Select an intervention plan to link."
            />
            <fe-spinner v-if="isLoading" class="spinner"/>
            <div v-else class="mt-3">
                <div class="d-flex align-center mb-2">
                    <v-checkbox 
                        v-model="onlyShowActive" 
                        hide-details 
                        :ripple="false"
                        class="interventions-to-link-input"
                    />
                    Only show interventions for the current school year
                </div>
                <v-list>
                    <v-radio-group v-model="selectedIntervention" hide-details class="interventions-to-link-input">
                        <v-list-item v-for="item in filteredItems" 
                            class="interventions-to-link mb-3" 
                            :class="{'interventions-to-link-selected': selectedIntervention === item}"
                            @click="selectedIntervention = item"
                        >
                            <v-radio class="interventions-to-link-radio" :value="item" :ripple="false"></v-radio>
                            <div class="mb-2">{{ item.intervention_plan_name }}</div>
                            <div class="interventions-to-link-subtext" style="margin-left: 28px;">
                                {{ $dayjs(item.intervention_plan_start_date).format('M/D/YYYY') }} - {{ $dayjs(item.intervention_plan_end_date).format('M/D/YYYY') }} 
                                <span class="mx-2">|</span> {{ item.intervention_level_name }}
                                <fe-tooltip :disabled="item.intervention_type_name.length < 30" :tooltip="item.intervention_type_name">
                                    <span class="mx-2">|</span> {{ getType(item.intervention_type_name) }}
                                </fe-tooltip>
                                <span class="mx-2">|</span> {{ item.intervention_category_name }}
                            </div>
                        </v-list-item>
                    </v-radio-group>
                </v-list>
        
                <error-state type="Intervention Plans" v-model="showErrorState"></error-state>
                <error-state type="Interventions" usage="error" v-model="showWarningState"></error-state>

                <div v-if="existing.length">
                    Already added to this form:
                    <v-list>
                        <v-list-item v-for="linked in existing" class="interventions-to-link mb-3">
                            <div class="mb-2">{{ linked.plan_name }}</div>
                            <div class="interventions-to-link-subtext">
                                {{ $dayjs(linked.intervention_plan_intervention_start_date).format('M/D/YYYY') }} - {{ $dayjs(linked.intervention_plan_intervention_end_date).format('M/D/YYYY') }} 
                                <span class="mx-2">|</span> {{ linked.intervention_level_name }}
                                <fe-tooltip :disabled="linked.intervention_type_name.length < 30" :tooltip="linked.intervention_type_name">
                                    <span class="mx-2">|</span> {{ getType(linked.intervention_type_name) }}
                                </fe-tooltip>
                                <span class="mx-2">|</span> {{ linked.intervention_category_name }}
                            </div>
                        </v-list-item>
                    </v-list>
                </div>
            </div>
        </fe-dialog>
    </div>
</template>

<script>
    import ErrorState from './ErrorState'
    import Banner from '@/components/common/Banner' 
    import {mapState} from 'vuex'

    export default {
        name: 'LinkInterventionDecisionRationale',

        components: {
            ErrorState,
            Banner,
        },

        props: {
            value: {
                type: Boolean, 
                default: false
            },
            formInstance: {
                type: Object,
                default: () => ({}),
                required: true
            },
            fieldRec: {
                type: Object,
                default: () => ({})
            },
            linkedDecisionRationale: {
                type: Array,
                default: () => []
            }
        },

        computed: {
            ...mapState('global', ['currentYear']),
            filteredItems() {
                let items = this.existing.length ? this.items.filter(mainItem => {
                    return !this.existing.some(filterItem => filterItem.intervention_id === mainItem.intervention_id)
                }) : this.items
                if(this.onlyShowActive) items = items.filter(x => x.school_year_id === this.currentYear.id)
                return items
            },
            showErrorState() {
                return !this.isLoading && !this.filteredItems.length && this.localValue
            },
        },  

        watch: {
            value(v) {
                this.localValue = v
                if (v) {
                    this.getInterventions()
                }
            },
            localValue(v) {
                this.$emit('input', v)
            },
            selectedIntervention(v) {
                if(v) {
                    this.showBanner = false
                }
            },
        },

        data() {
            return {
                onlyShowActive: true,
                localValue: false,
                existing: [],
                items: [],
                forms: [],
                selectedIntervention: null,
                isLoading: true,
                showBanner: false,
                showWarningState: false,
            }
        },

        mounted() {
            if(this.localValue) {
                this.getInterventions()
            }
        },

        methods: {
            async getInterventions() {
                let p = { student_id: this.formInstance.first_student_id }
                let response = await this.$axios.get(this.$models.getUrl('getLinkedInterventions', 'read') + '&' + this.$objectToParams(p))
                this.items = response.data ? response.data.student_intervention_plan : []
                if(!response.data) this.showWarningState = true
                let existing = this.linkedDecisionRationale.length ? this.items.filter(mainItem =>
                    this.linkedDecisionRationale.some(filterItem => filterItem.id === mainItem.intervention_id)
                ).map(mainItem => ({
                    ...mainItem,
                    plan_name: this.linkedDecisionRationale.find(filterItem => filterItem.id === mainItem.intervention_id)?.plan_name
                })) : []
                this.existing = existing.length ? existing.filter((item, index) =>
                    existing.findIndex(x => x.intervention_id === item.intervention_id) === index
                ) : existing
                this.isLoading = false
                return this.existing
            },
            getType(name) {
                if(name.length < 30) return name
                return name.slice(0,27) + '...'
            },
            doUnlink(rec) {
                let me = this
                let found = this.linkedDecisionRationale.find(x => x.form_field_id === this.fieldRec?.form_field_id)
                this.$messageBox({
                    title: 'Confirm',
                    persistent: true,
                    maxWidth: 400,
                    message: 'Are you sure you want to unlink this smartFORM?',
                    actions: [{
                        text: 'Cancel'
                    }, {
                        text: 'Unlink',
                        usage: 'danger',
                        onClick: () => {
                            me.$axios.delete('/api/v3/form/form-instance-intervention/remove?id=' + found?.form_instance_intervention_id)
                                .then(r=> {
                                    this.$ecResponse(r)
                                    me.getInterventions()
                                    this.$emit('reload')
                                })
                        }
                    }]
                })
            },
            doLink() {
                if(!this.selectedIntervention) {
                    this.showBanner = true
                } else {
                    let found = this.linkedDecisionRationale?.find(x => x.form_field_id === this.fieldRec?.form_field_id)
                    let p = {
                        formInstanceId: this.formInstance.form_instance_id,
                        formFieldId: this.fieldRec.form_field_id,
                        interventionId: this.selectedIntervention.intervention_id,
                        formInstanceInterventionId: found?.form_instance_intervention_id || null
                    }
                    this.$axios.put('/api/v3/form/form-instance-intervention/create', p)
                        .then(response => {
                            this.$ecResponse(response)
                        })
                        .finally(() => {
                            this.localValue = false
                            this.$emit('reload')
                        })
                }
            }
        }
    }
</script>

<style lang="scss" scoped>
    .interventions-to-link {
        border: 1px solid #E0E1E6;
        border-radius: 8px;
        max-height: 90px;
        display: flex;
        flex-wrap: wrap;
        height: 100%;
        align-items: flex-start;
        padding: 20px;
        font-size: 14px;
        &-subtext {
            color: #495B8F;
            flex-basis: 100%;    
            flex-direction: row;
            display: flex;
        }
        &-radio {
            ::v-deep i {
                font-size: 20px;
            }
            ::v-deep .v-input--selection-controls__input {
                margin-right: 4px;
            }
        }
        &-selected {
            border: 1px solid #006C90;
        }
        &-input {
            ::v-deep .v-input__slot {
                padding: 0 !important;
            }
        }
    }
</style>
